import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface CustomerNote {
  id: string;
  customer_id: string;
  content: string;
  created_by: string | null;
  created_at: string;
}

export const useCustomerNotes = (customerId: string | undefined) => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["customer_notes", customerId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("customer_notes")
        .select("*")
        .eq("customer_id", customerId!)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as CustomerNote[];
    },
    enabled: !!user && !!customerId,
  });
};

export const useCreateCustomerNote = () => {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async ({ customer_id, content }: { customer_id: string; content: string }) => {
      const { data: companyId, error: cidErr } = await supabase.rpc("my_company_id");
      if (cidErr) throw cidErr;
      const { error } = await supabase.from("customer_notes").insert({
        customer_id,
        company_id: companyId,
        content,
        created_by: user?.id,
      });
      if (error) throw error;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["customer_notes", vars.customer_id] });
    },
  });
};
